import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ObjectSchema, object, boolean, string } from 'yup'
import { LoginContract } from '@/Account/application/Login'
import { LoginRepository } from '@/Account/domain/LoginRepository'
import { useAuth } from '@/Shared/ui/auth'
import {
  nonEmptyStringSchema,
  useMetaResponseHandler,
  validateMetaResponse,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'
import { navigationRoutes, openNavigationRoutes } from '@/Shared/ui/routers'
import { LoginTranslation } from '@/Account/ui/accountTranslation'
import { legacyLoginSiteUrl } from '@/Shared/infrastructure'
import { formTranslation } from '@/Shared/ui/sharedTranslation'

export interface LoginForm {
  username: string
  password: string
  rememberUser?: boolean
}

export interface OtpForm {
  otp: string
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<LoginForm> =>
  object({
    username: nonEmptyStringSchema(t, 75)
      .required(),
    password: nonEmptyStringSchema(t).required(),
    rememberUser: boolean().optional(),
  })

const otpSchema = (t: TFunction<'translation', undefined>): ObjectSchema<{ otp: string }> =>
  object({
    otp: string()
      .required(t(formTranslation.errors.fieldRequired))
  })

export interface IUseLoginController {
  form: UseFormReturn<LoginForm>
  otpForm: UseFormReturn<{ otp: string }>
  login: (data: LoginForm) => Promise<boolean>
  validateOtp: (otp: string, guid: string) => Promise<boolean>
  resendOtp: () => Promise<string>
}

export const useLoginController = (
  repository: LoginRepository,
): IUseLoginController => {
  const { t } = useTranslation()
  const { handleMetaResponse } = useMetaResponseHandler()
  const { startLoading, stopLoading } = useLoader()

  const navigate = useNavigate()
  const { storeToken } = useAuth()

  const form = useForm<LoginForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onBlur',
  })

  const otpForm = useForm<{ otp: string }>({
    resolver: yupResolver(otpSchema(t)),
    mode: 'onBlur',
  })

  const login = async (data: LoginForm): Promise<boolean> => {
    startLoading()

    const response = await LoginContract(repository).login(data)

    stopLoading()

    const valid = validateMetaResponse(response?.meta)

    if (valid) {
      if (response.data?.token) storeToken(response.data.token)

      if (response.data?.isLegacyUser) {
        globalThis.location.replace(`${legacyLoginSiteUrl}/?type=100`)
      } else if (response.data?.isOtpRequired) {
        navigate(openNavigationRoutes.otpAuth, {
          state: {
            origin: openNavigationRoutes.login,
            otpGuid: response.data.otpGuid,
            otpEmail: response.data.otpEmail
          },
        })
      } else {
        globalThis.location.href = navigationRoutes.home
      }
    } else {
      // Error management
      if (response?.meta.messages.length > 0) {
        form.setError("root", { type: "custom", message: response?.meta?.messages[0].description })
        form.setError("username", { type: "custom" })
        form.setError("password", { type: "custom" })
      }
    }

    return valid
  }

  const validateOtp = async (otp: string, guid: string): Promise<boolean> => {
    startLoading()

    const response = await LoginContract(repository).validateOtp(otp, guid)

    stopLoading()

    return (
      validateMetaResponse(response?.meta) && (response?.data ?? false)
    )
  }

  const resendOtp = async (): Promise<string> => {
    startLoading()

    const response = await LoginContract(repository).resendOtp()

    stopLoading()

    const valid = handleMetaResponse(response?.meta, undefined, {
      notifySuccess: true,
      successMessage: t(LoginTranslation.otp.resendCodeToast),
    })

    return valid ? response.data ?? '' : ''
  }


  return {
    form,
    otpForm,
    login,
    validateOtp,
    resendOtp,
  }
}
